import { setPianoConfig } from 'services/Piano';
import { setHeadData } from 'utils/metaDataUtils/headData';
import type { Hub } from 'interfaces/Home';
import type { GetStaticProps } from 'next/types';
import { educationLayoutCounts, Microsites, SectionTypes } from 'constants/constants';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import getSection from 'api/Section/getSection';
import {
  type Disclosure,
  getMicrositeConfig,
  type InnerSectionConfig,
  type MicrositeConfig,
  type SectionsConfig,
} from 'api/MicrositeConfig/getMicrositeConfig';
import { recommendsLogo } from 'icons/RecommendsLogo';
import type { HeaderNav } from 'interfaces/navigation/Header';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import NotFoundError from 'interfaces/errors/NotFoundError';
import getMenu from 'api/Header/getMenu';
import { populateThumbnailToMenuItems } from 'api/Topic/getTopicMenuItemThumbnails';
import EduSubsectionPage from 'pages/education/SubsectionPage';
import { getEduRankings } from 'api/EduRanking/getEduRankings';
import type { AdditionalRanking, EduAdWigetsByPosition, EduCategory, EduRanking } from 'interfaces/EduRanking';
import SectionPage from 'pages/SectionPage';
import type { Article, RecentArticles } from 'interfaces/content/articles/Articles';
import { getArticleBySlug } from 'api/Article/getArticleBySlug';
import { getFirstParagraph } from 'utils/parsingUtils/serverSideParsing';
import getAdWigetsByAdTag from 'api/EduRanking/getAdWidgets';
import { PageType } from 'interfaces/content/articles/Post';
import { calculatePagePagination, type PagePagination } from 'utils/miscUtils/calculatePageInfo';
import getFooter from 'api/Footer/getFooter';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallback from 'lib/fallbackData/commonData';
import recommendsFallback from 'lib/fallbackData/recommendsData';
import setEducationPageAdConfig from 'services/Ad/config/setEducationPageAdConfig';
import { PianoExperiencePageType } from '@/app_lib/types/piano/Paywall';

export interface PageProps {
  section: Hub;
  latestArticles: RecentArticles;
  sectionsConfig: SectionsConfig;
  rankings: EduRanking[];
  additionalRankings: AdditionalRanking[];
  pageType: PageType;
  pagination: PagePagination;
  adWidgets: EduAdWigetsByPosition;
  childRankingCategories: InnerSectionConfig['childRankingCategories'];
}

const EduSectionAndCategory = ({
  section,
  sectionsConfig,
  latestArticles,
  rankings,
  childRankingCategories,
  additionalRankings,
  pageType,
  pagination,
  adWidgets,
}: PageProps) => {
  if (pageType === PageType.EDU_CATEGORY) {
    return (
      <EduSubsectionPage
        section={section}
        rankings={rankings}
        additionalRankings={additionalRankings}
        adWidgets={adWidgets}
        childRankingCategories={childRankingCategories}
        pagination={pagination}
      />
    );
  }

  return (
    <SectionPage
      section={section}
      sectionsConfig={sectionsConfig}
      latestArticles={latestArticles}
      pinnedArticles={[]}
      platform={Microsites.Education}
      pagination={pagination}
      subsectionsLatest={[]}
    />
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { section: sectionSlug, pageNum } = context.params as { section: string; pageNum: string };
    const currentPageNum = parseInt(pageNum || '1', 10);

    if (pageNum === '1' || Number.isNaN(currentPageNum) || currentPageNum < 1) {
      throw new NotFoundError(`[getSubsectionPageNumber] invalid page number: '${currentPageNum}'`);
    }

    const footer = await safeAsync(() => getFooter(Microsites.Recommends), fallback.footer);
    const educationData: MicrositeConfig = await safeAsync(
      () => getMicrositeConfig(Microsites.Education),
      recommendsFallback.recommendsData,
    );
    const menu: HeaderNav = await safeAsync(
      () =>
        process.env.FEATURE_FLAG_DISABLE_RECOMMENDS
          ? getMenu(
              'LEFT_MENU_COM',
              'RIGHT_MENU_COM',
              'TOPIC_NAVIGATION_COM',
              Microsites.Recommends,
              null,
              'RIGHT_MENU_COM',
            )
          : getMenu(
              'LEFT_MENU_RECOMMENDS',
              'LEFT_MENU_COM',
              'TOPIC_NAVIGATION_RECOMMENDS_V2',
              Microsites.Recommends,
              recommendsLogo,
              'RIGHT_MENU_COM',
            ),
      recommendsFallback.headerNav,
    );
    await safeAsync(() => populateThumbnailToMenuItems(menu.topicNavigation), null);

    const disclosure: Disclosure | undefined | null = educationData.disclosure || null;

    const educationSections: SectionsConfig = educationData.sections as SectionsConfig;

    if (Object.keys(educationSections).indexOf(sectionSlug) === -1) throw new NotFoundError('Section not found');
    const sectionConfig = educationSections[sectionSlug];

    let additionalRankings: Article[] = [];
    let rankings: EduRanking[] = [];
    let categories: EduCategory[] = [];
    let pageType = PageType.SECTION;
    let hasArcherAds = false;
    let widgets = null;
    let widgetCss = null;

    // the article sections have a new "type" property in the destination config with the value "articlesSection"
    if (sectionConfig?.type !== 'articlesSection') {
      pageType = PageType.EDU_CATEGORY;
      // will be updated when backend is changed
      const categoryQueryValMap: { [key: string]: string } = {
        business: 'BUSINESS',
        health: 'HEALTH',
        'information-technology': 'INFORMATION_TECHNOLOGY',
      };

      const rankingsObj = await safeAsync(
        () => getEduRankings(categoryQueryValMap[sectionSlug]),
        recommendsFallback.rankingsObj,
      );
      rankings = (rankingsObj.rankings || []).filter(({ latestList }) => latestList && latestList.length > 0);
      categories = rankingsObj.categories;

      if (sectionConfig.additionalRankingsList) {
        const results = await Promise.allSettled(
          sectionConfig.additionalRankingsList?.map(async (rankingSlug) => getArticleBySlug(rankingSlug)),
        );
        additionalRankings = (
          results.filter(({ status }) => status === 'fulfilled') as PromiseFulfilledResult<Article>[]
        ).map(({ value: rankingArticle }) => ({
          ...rankingArticle,
          description: safe(() => getFirstParagraph(rankingArticle.content), ''),
          titleWithYear: rankingArticle.title,
        }));
      }
    }

    const numPostsPerPage =
      pageType === PageType.EDU_CATEGORY ? educationLayoutCounts.subsectionLatest : educationLayoutCounts.sectionLatest;

    const section = (await safeAsync(
      () =>
        getSection(
          sectionConfig.slug,
          numPostsPerPage,
          (currentPageNum - 1) * numPostsPerPage,
          Microsites.Education,
          educationSections,
        ),
      recommendsFallback.section,
    )) as Hub;

    const pageAdConfig = setEducationPageAdConfig(section);

    const latestArticles = { pageInfo: section.pageInfo, posts: section.posts };

    if (currentPageNum > 1 && currentPageNum > latestArticles.pageInfo.totalCount) {
      throw new NotFoundError(`[getSubsectionPageNumber] invalid page number: '${currentPageNum}'`);
    }

    const category = categories.find((categ) => categ.slug === sectionSlug);
    section.title = category?.name || section.title;
    section.databaseId = category ? category.databaseId : section.databaseId;
    const dataLayer = safe(
      () => setPageGtmConfig({ ...section, platformCD: Microsites.Education }),
      fallback.dataLayer,
    );
    section.canonicalUrl = section.canonicalUrl.replace(section.slug, sectionSlug);

    const isSection = section.sectionType === SectionTypes.SECTION;
    const totalArticles = isSection ? latestArticles.pageInfo.totalCount - 10 : latestArticles.pageInfo.totalCount;

    const pageNavigation = calculatePagePagination(currentPageNum, totalArticles, numPostsPerPage, 'section');

    if (pageType === PageType.EDU_CATEGORY) {
      section.prefixedDatabaseId = category?.prefixedDatabaseId || null;
    }

    const headData = safe(
      () =>
        setHeadData(
          {
            ...section,
            canonicalUrl:
              currentPageNum && currentPageNum > 1
                ? `${section.canonicalUrl}page/${currentPageNum}/`
                : section.canonicalUrl,
            image: category?.image?.src ? category?.image?.src : '',
            link: section.canonicalUrl,
            pageType,
            platformCD: Microsites.Education,
            titlePrefix: pageNavigation.pageInfo,
          },
          true,
          'Fortune Education',
        ),
      fallback.headData,
    );
    const subMenu = menu?.topicNavigation.find((menuItem) => menuItem.label === section.name)?.subMenu || null;

    const adTagSlug = category?.adTag;
    if (pageType === PageType.EDU_CATEGORY && adTagSlug) {
      ({ hasArcherAds, widgets, widgetCss } = await safeAsync(
        () => getAdWigetsByAdTag(adTagSlug),
        recommendsFallback.adWidgets,
      ));
      if (widgetCss) {
        headData.widgetCss = widgetCss;
      }
    }

    return {
      props: {
        adWidgets: widgets,
        additionalRankings,
        childRankingCategories: sectionConfig.childRankingCategories || [],
        dataLayer,
        footer,
        hasArcherAds,
        headData,
        headerNav: { ...menu, disclosure },
        latestArticles,
        pageAdConfig,
        pageType,
        pagination: pageNavigation,
        pianoPageConfig: setPianoConfig({ pageType: PianoExperiencePageType.Default, pageData: {} }),
        rankings,
        redVenture: true,
        section,
        sectionsConfig: educationSections,
        subMenu,
      },
      revalidate: 60, // In seconds
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};

export const getStaticPaths = async () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [],
    };
  }

  const educationSections = (await getMicrositeConfig(Microsites.Education)).sections as SectionsConfig;
  const paths = Object.keys(educationSections).map((slug) => ({
    params: {
      section: slug,
    },
  }));

  return {
    fallback: 'blocking',
    paths,
  };
};

export default EduSectionAndCategory;
